import type { TableCell, CellContent, CommitmentTerm } from './types'

/**
 * Validate that a URL is safe for use in href attributes.
 * Prevents XSS via javascript:, data:, and other unsafe protocols.
 */
export function isSafeUrl(url: string): boolean {
  // Allow relative paths (but not protocol-relative URLs like //evil.com)
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true
  }

  // Allow anchor links
  if (url.startsWith('#')) {
    return true
  }

  try {
    const parsed = new URL(url)
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol)
  } catch {
    // If URL parsing fails and it's not a relative path, consider it unsafe
    return false
  }
}

/**
 * Get the display content for a cell based on its variant and current pricing/commitment state
 */
export function getCellContent(
  cell: TableCell,
  displayPeriod: 'hourly' | 'monthly',
  displayCommitmentTerm: CommitmentTerm,
): CellContent {
  const {
    variant,
    primaryText,
    secondaryText,
    hourlyPrice,
    monthlyPrice,
    commitment12,
    commitment24,
    commitment36,
  } = cell

  switch (variant) {
    case 'blank':
      return { primary: '' }
    case 'price':
      // Use the displayPeriod for showing hourly or monthly price with animation
      if (displayPeriod === 'monthly') {
        return {
          primary: monthlyPrice || '',
          secondary: monthlyPrice ? 'per month' : undefined,
        }
      }
      return {
        primary: hourlyPrice || '',
        secondary: hourlyPrice ? 'per hour' : undefined,
      }
    case 'commitment':
      // Use the displayCommitmentTerm for showing commitment price with animation
      const commitmentPrices: Record<CommitmentTerm, string | null | undefined> = {
        '12': commitment12,
        '24': commitment24,
        '36': commitment36,
      }
      const commitmentPrice = commitmentPrices[displayCommitmentTerm] || ''
      return {
        primary: commitmentPrice,
        secondary: commitmentPrice ? 'per hour' : undefined,
      }
    default:
      return {
        primary: primaryText || '',
        secondary: secondaryText || undefined,
      }
  }
}
